<template>
  <div>
    <v-row no-gutters class="mb-8 px-8">
      <v-col xs="0" sm="0" md="0" lg="1" xl="1"/>
      <v-col xs="12" sm="12" md="12" lg="10" xl="10" class="pt-8 d-flex flex-column">
        <!-- TITLE -->
        <v-row class="d-flex pb-8">
          <v-col class="d-flex justify-center text-h3 font-weight-light text-center">
            {{ $t('title.exposure') }}
          </v-col>
        </v-row>

        <v-row class="d-flex">
          <!-- LEFT: EXPOSURE -->
          <v-col cols="12" md="6">
            <v-card class="ma-2 pa-2 rounded-lg">
              <h2 class="mb-2">{{ $t('exposure.exposure.title') }}</h2>
              <v-data-table
                :headers="exposureHeaders"
                :items="formattedExposureItems"
                :items-per-page="1000"
                hide-default-header
                hide-default-footer
                disable-sort
              >
                <template v-slot:header="{ props }">
                  <thead>
                    <tr>
                      <th
                        v-for="header in props.headers"
                        :key="header.value"
                        class="text-center white--text"
                        style="background-color: #2a2a2a; font-size: 15px"
                      >
                        {{ header.text }}
                      </th>
                    </tr>
                  </thead>
                </template>

                <template v-slot:item="{ item }">
                  <tr>
                    <td class="text-center">
                      {{ item.date }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.receivable, '#FFC107')"
                    >
                      {{ item.receivable }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.payable, '#FF6D00')"
                    >
                      {{ item.payable }}
                    </td>
                    <td class="text-center" :style="highlightNet(item.net)">
                      {{ item.net }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>

            <v-card class="ma-2 pa-2 pb-4 rounded-lg">
              <v-data-table
                :headers="exposureTotalsHeaders"
                :items="exposureTotalsItems"
                hide-default-footer
                disable-sort
                dense
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td
                      :class="[
                        'text-left',
                        'font-weight-bold',
                        item.title === 'VaR (FX Exposure)' ? 'text--disabled' : ''
                      ]"
                    >
                      <span v-html="item.title" />
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.receivable, '#FFC107')"
                    >
                      {{ item.receivable }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.payable, '#FF6D00')"
                    >
                      {{ item.payable }}
                    </td>
                    <td class="font-weight-bold text-center">
                      {{ item.total }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <!-- RIGHT: HEDGE -->
          <v-col cols="12" md="6">
            <v-card class="ma-2 pa-2 rounded-lg">
              <div class="d-flex align-center justify-space-between">
                <h2 class="mb-2"> {{ $t('exposure.hedge.title') }} </h2>
                <div>
                  <span class="mr-2">{{ $t('exposure.hedge.maxHedgeRatio') }}</span>
                  <span class="mr-4 font-weight-bold"> {{ hedgeRatio }}</span>
                </div>
              </div>
              <v-data-table
                :headers="hedgeHeaders"
                :items="formattedHedgeItems"
                :items-per-page="1000"
                hide-default-header
                hide-default-footer
              >
                <template v-slot:header="{ props }">
                  <thead>
                    <tr>
                      <th
                        v-for="header in props.headers"
                        :key="header.value"
                        class="text-center white--text"
                        style="background-color: #2a2a2a; font-size: 15px"
                      >
                        <span v-html="header.text"></span>
                      </th>
                    </tr>
                  </thead>
                </template>

                <template v-slot:item="{ item }">
                  <tr>
                    <td class="text-center">
                      {{ item.date }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.sell, '#FF6D00')"
                    >
                      {{ item.sell }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.buy, '#FFC107')"
                    >
                      {{ item.buy }}
                    </td>
                    <td class="text-center" :style="highlightNet(item.net)">
                      {{ item.net }}
                    </td>
                    <td class="text-center">
                      <v-btn
                        v-model="item.button"
                        x-small
                        color="primary"
                        @click="showBuyDialog = true"
                      >
                        {{ $t('exposure.viewOrder') }}
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>

            <v-card class="ma-2 pa-2 rounded-lg">
              <v-data-table
                :headers="hedgeTotalsHeaders"
                :items="hedgeTotalsItems"
                hide-default-footer
                disable-sort
                dense
              >
                <template v-slot:item="{ item }">
                  <tr>
                    <td class="font-weight-bold">
                      <span v-html="item.title" />
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.sell, '#FF6D00')"
                    >
                      {{ item.sell }}
                    </td>
                    <td
                      class="text-center"
                      :style="highlight(item.buy, '#FFC107')"
                    >
                      {{ item.buy }}
                    </td>
                    <td class="text-center font-weight-bold">
                      {{ item.totalHedge }}
                    </td>
                    <td class="text-center font-weight-bold">
                      {{ item.totalFX }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col xs="0" sm="0" md="0" lg="1" xl="1"/>
    </v-row>

    <!-- BUY DIALOG -->
    <BuyDialog v-model="showBuyDialog"/>
  </div>
</template>


<script>
import { mapState, mapActions } from "pinia"
import { useSettingsStore, useRiskStore } from "./../store/index"
import BuyDialog from '@/components/BuyDialog.vue'

export default {
  components: { 
    BuyDialog
  },
  data () {
    return {
      exposureHeaders: [
        { text: this.$t('extras.date'), value: 'date', align: "center", width: "25%" },
        { text: this.$t('exposure.exposure.receivable'), value: 'receivable', align: "center", width: "25%" },
        { text: this.$t('exposure.exposure.payable'), value: 'payable', align: "center", width: "25%" },
        { text: this.$t('exposure.exposure.netExposure'), value: 'net', align: "center", width: "25%" },
      ],
      exposureTotalsHeaders: [
        { text: this.$t('exposure.exposure.total'), value: "title", width: "25%" },
        { text: this.$t('exposure.exposure.downsideRisk'), value: 'receivable', align: "center", width: "25%" },
        { text: this.$t('exposure.exposure.upsideRisk'), value: 'payable', align: "center", width: "25%" },
        { text: "", value: "total", align: "center", width: "25%" },
      ],
      exposureItems: [],
      hedgeHeaders: [
        { text: this.$t('extras.date'), value: 'date', align: "center", width: "20%" },
        { text: this.$t('exposure.hedge.ndfSell'), value: 'sell', align: "center", width: "20%" },
        { text: this.$t('exposure.hedge.ndfBuy'), value: 'buy', align: "center", width: "20%" },
        { text: `${this.$t('exposure.exposure.netExposure')}<br>${this.$t('exposure.hedge.afterDerivatives')}`, value: 'net', align: "center", width: "20%" },
        { text: "", value: 'button', align: "center", width: "20%" },
      ],
      hedgeTotalsHeaders: [
        { text: this.$t('exposure.hedge.totalDerivatives'), value: "title", width: "20%" },
        { text: " ", value: 'sell', align: "center", width: "20%" },
        { text: " ", value: 'buy', align: "center", width: "20%" },
        { text: " ", value: "totalHedge", align: "center", width: "20%" },
        { text: " ", value: "totalFX", align: "center", width: "20%" },
      ],
      hedgeItems: [],
      hedgeRatio: "0.7",
      showBuyDialog: false,
    }
  },
  mounted() {
    this.fetchExposureData()
  },
  watch: {
  },
  computed: {
    ...mapState(useSettingsStore, [
      "isMockDataOn"
    ]),
    formattedExposureItems () {
      const grouped = {}

      this.exposureItems.forEach(entry => {
        const date = entry.Date
        const amount = Number(entry.Amount)

        if (!grouped[date]) {
          grouped[date] = {
            date: date,
            receivable: null,
            payable: null,
            net: 0
          }
        }

        if (amount > 0) {
          grouped[date].receivable = amount
        } else if (amount < 0) {
          grouped[date].payable = amount
        }

        grouped[date].net += amount
      })

      return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date))
    },
    formattedHedgeItems () {
      return this.hedgeItems.map(item => ({
        date: item.date,
        sell: item.ndf_sell_volume || null,
        buy: item.ndf_buy_volume || null,
        net: item.net_exposure_after_hedge
      }))
    },
    totalReceivables () {
      return this.formattedExposureItems.reduce((sum, i) => sum + (i.receivable || 0), 0)
    },
    totalPayments () {
      return this.formattedExposureItems.reduce((sum, i) => sum + (i.payable || 0), 0)
    },
    totalExposure () {
      return this.formattedExposureItems.reduce((sum, i) => sum + Math.abs(i.net || 0), 0)
    },
    exposureTotalsItems () {
      return [
        {
          title: this.$t('exposure.exposure.receivableVolume'),
          receivable: this.totalReceivables,
          payable: '-',
          total: ''
        },
        {
          title: this.$t('exposure.exposure.payableVolume'),
          receivable: '-',
          payable: this.totalPayments,
          total: ''
        },
        {
          title: this.$t('exposure.exposure.fxExposure'),
          receivable: '',
          payable: '',
          total: this.totalExposure
        },
        {
          title: this.$t('exposure.exposure.var'),
          receivable: '-',
          payable: '',
          total: '',
        }
      ]
    },
    totalHedgeBuy () {
      return this.formattedHedgeItems.reduce((sum, i) => sum + (i.buy || 0), 0)
    },
    totalHedgeSell () {
      return this.formattedHedgeItems.reduce((sum, i) => sum + (i.sell || 0), 0)
    },
    totalNetAfterHedge () {
      return this.hedgeItems.reduce((sum, i) => sum + Math.abs(i.net_exposure_after_hedge || 0), 0)
    },
    totalHedgeVolume () {
      return this.totalHedgeBuy + this.totalHedgeSell
    },
    hedgeTotalsItems () {
      return [
        {
          title: this.$t('quoteInfo.sell'),
          sell: this.totalHedgeSell,
          buy: '-',
          totalHedge: '',
          totalFX: ''
        },
        {
          title: this.$t('quoteInfo.buy'),
          sell: '-',
          buy: this.totalHedgeBuy,
          totalHedge: '',
          totalFX: ''
        },
        {
          title: this.$t('exposure.hedge.hedge'),
          sell: '',
          buy: '',
          totalHedge: this.totalHedgeVolume,
          totalFX: ' '
        },
        {
          title: `${this.$t('exposure.exposure.fxExposure')}<br>${this.$t('exposure.hedge.afterDerivatives')}`,
          sell: '',
          buy: '',
          totalHedge: '',
          totalFX: this.totalNetAfterHedge
        },
      ]
    }
  },
  methods: {
    ...mapActions(useRiskStore, [
      "getExposureData"
    ]),
    fetchExposureData () {
      this.getExposureData(true)
        .then(response => {
          const data = response.data
          this.exposureItems = data
        })
        .catch(error => {
          console.log(error)
        })
      this.getExposureData(false)
        .then(response => {
          const data = response.data
          this.hedgeItems = data
        })
        .catch(error => {
          console.log(error)
        })
    },
    highlight(val, color) {
      return val !== '-' && val !== '' && val !== null ? `color: ${color}` : ''
    },
    highlightNet(val) {
      if (typeof val !== 'number') return ''
      if (val > 0) return 'color: #FFC107'
      if (val < 0) return 'color: #FF6D00'
      return ''
    },
    sendEmail () {
      const subject = encodeURIComponent(this.$t('buyDialog.accessRequest'))

      window.location.href = `mailto:support@clearfxai.com?subject=${subject}`
    }
  }
}
</script>

<style scoped>

</style>
